import React, { useCallback, useRef, useState } from 'react';
import {
  TextField,
  Button,
  IconButton,
  List,
  ListItem,
  ListItemIcon,
  ListItemText,
  ListItemSecondaryAction,
  Paper,
  Box,
  Typography,
  Divider,
} from '@material-ui/core';
import {
  Add as AddIcon,
  Delete as DeleteIcon,
  AttachFile as AttachFileIcon,
  Link as LinkIcon,
  PictureAsPdf as PdfIcon,
  InsertDriveFile as FileIcon,
  ErrorOutline as ErrorIcon,
} from '@material-ui/icons';

const ACCEPTED_TYPES = '.pdf';
const MAX_FILES = 5;
const MAX_TOTAL_BYTES = 80 * 1024 * 1024; // 80 MB

const getFileIcon = (name) => {
  if (!name) return <FileIcon style={{ color: '#6ab3d4' }} />;
  const ext = name.split('.').pop().toLowerCase();
  if (ext === 'pdf') return <PdfIcon style={{ color: '#d32f2f' }} />;
  return <FileIcon style={{ color: '#6ab3d4' }} />;
};

const formatBytes = (bytes) => {
  if (!bytes) return '';
  if (bytes < 1024) return `${bytes} B`;
  if (bytes < 1024 * 1024) return `${(bytes / 1024).toFixed(1)} KB`;
  return `${(bytes / (1024 * 1024)).toFixed(1)} MB`;
};

const isValidUrl = (url) => {
  try {
    new URL(url);
    return true;
  } catch {
    return false;
  }
};

const newFilesSize = (attachments) =>
  attachments.filter((a) => a.file).reduce((sum, a) => sum + (a.size || 0), 0);

const LinksAttachmentsSection = ({ attachments, links, setFieldValue }) => {
  const fileInputRef = useRef(null);
  const [newUrl, setNewUrl] = useState('');
  const [newLabel, setNewLabel] = useState('');
  const [urlError, setUrlError] = useState('');
  const [fileError, setFileError] = useState('');

  const newFiles = attachments.filter((a) => a.file);
  const currentNewSize = newFilesSize(attachments);
  const atCountLimit = newFiles.length >= MAX_FILES;
  const atSizeLimit = currentNewSize >= MAX_TOTAL_BYTES;
  const uploadDisabled = atCountLimit || atSizeLimit;

  const handleFileSelect = useCallback((e) => {
    const files = Array.from(e.target.files);
    e.target.value = '';

    const countAfter = newFiles.length + files.length;
    if (countAfter > MAX_FILES) {
      setFileError(`Máximo ${MAX_FILES} archivos permitidos. Solo puedes agregar ${MAX_FILES - newFiles.length} más.`);
      return;
    }

    const sizeAfter = currentNewSize + files.reduce((s, f) => s + f.size, 0);
    if (sizeAfter > MAX_TOTAL_BYTES) {
      setFileError(`El tamaño total no puede superar 80 MB. Disponible: ${formatBytes(MAX_TOTAL_BYTES - currentNewSize)}.`);
      return;
    }

    setFileError('');
    const newAttachments = files.map((file) => ({
      file,
      name: file.name,
      size: file.size,
      type: file.type,
    }));
    setFieldValue('attachments', [...attachments, ...newAttachments]);
  }, [attachments, newFiles, currentNewSize, setFieldValue]);

  const removeAttachment = useCallback((index) => {
    setFileError('');
    setFieldValue('attachments', attachments.filter((_, i) => i !== index));
  }, [attachments, setFieldValue]);

  const addLink = useCallback(() => {
    const trimmedUrl = newUrl.trim();
    if (!trimmedUrl || !newLabel.trim()) return;
    if (!isValidUrl(trimmedUrl)) {
      setUrlError('URL inválida. Ej: https://ejemplo.com');
      return;
    }
    setFieldValue('links', [...links, { url: trimmedUrl, label: newLabel.trim() }]);
    setNewUrl('');
    setNewLabel('');
    setUrlError('');
  }, [newUrl, newLabel, links, setFieldValue]);

  const removeLink = useCallback((index) => {
    setFieldValue('links', links.filter((_, i) => i !== index));
  }, [links, setFieldValue]);

  const handleUrlChange = (e) => {
    setNewUrl(e.target.value);
    if (urlError) setUrlError('');
  };

  return (
    <Paper style={{ padding: 16 }}>
      {/* Archivos adjuntos */}
      <Box marginBottom={2}>
        <Typography variant="subtitle1" style={{ fontWeight: 600, marginBottom: 8 }}>
          Archivos adjuntos
        </Typography>
        <input
          ref={fileInputRef}
          type="file"
          accept={ACCEPTED_TYPES}
          multiple
          style={{ display: 'none' }}
          onChange={handleFileSelect}
        />
        <Box display="flex" alignItems="center" style={{ gap: 12, flexWrap: 'wrap' }}>
          <Button
            variant="contained"
            size="small"
            startIcon={<AttachFileIcon />}
            style={{ backgroundColor: uploadDisabled ? '#b0b0b0' : '#6ab3d4', color: '#fff' }}
            onClick={() => !uploadDisabled && fileInputRef.current.click()}
            disabled={uploadDisabled}
          >
            Subir archivo
          </Button>
          <Typography variant="caption" style={{ color: '#777' }}>
            {`Solo PDF · Máx. ${MAX_FILES} archivos · Máx. 80 MB en total`}
          </Typography>
          <Typography variant="caption" style={{ color: newFiles.length >= MAX_FILES ? '#d32f2f' : '#777' }}>
            {`${newFiles.length}/${MAX_FILES} archivos · ${formatBytes(currentNewSize) || '0 B'} / 80 MB`}
          </Typography>
        </Box>

        {fileError && (
          <Box display="flex" alignItems="center" style={{ marginTop: 8, gap: 4, color: '#d32f2f' }}>
            <ErrorIcon fontSize="small" />
            <Typography variant="caption" style={{ color: '#d32f2f' }}>
              {fileError}
            </Typography>
          </Box>
        )}

        {uploadDisabled && !fileError && (
          <Box display="flex" alignItems="center" style={{ marginTop: 8, gap: 4 }}>
            <ErrorIcon fontSize="small" style={{ color: '#d32f2f' }} />
            <Typography variant="caption" style={{ color: '#d32f2f' }}>
              {atCountLimit
                ? `Límite de ${MAX_FILES} archivos alcanzado.`
                : 'Límite de 80 MB alcanzado.'}
            </Typography>
          </Box>
        )}

        {attachments.length > 0 ? (
          <List dense style={{ marginTop: 8 }}>
            {attachments.map((att, index) => (
              <ListItem key={index} divider
                button={!!att.url}
                component={att.url ? 'a' : 'li'}
                href={att.url || undefined}
                target={att.url ? '_blank' : undefined}
                rel={att.url ? 'noopener noreferrer' : undefined}
                style={{ paddingRight: 48 }}
              >
                <ListItemIcon style={{ minWidth: 36 }}>
                  {getFileIcon(att.name)}
                </ListItemIcon>
                <ListItemText
                  primary={att.name || 'Archivo adjunto'}
                  secondary={att.size ? formatBytes(att.size) : att.url ? 'Archivo guardado' : ''}
                />
                <ListItemSecondaryAction>
                  <IconButton edge="end" size="small" onClick={() => removeAttachment(index)}>
                    <DeleteIcon style={{ color: 'red' }} />
                  </IconButton>
                </ListItemSecondaryAction>
              </ListItem>
            ))}
          </List>
        ) : (
          <Typography variant="body2" color="textSecondary" style={{ marginTop: 8 }}>
            No hay archivos adjuntos
          </Typography>
        )}
      </Box>

      <Divider style={{ margin: '16px 0' }} />

      {/* Links */}
      <Box>
        <Typography variant="subtitle1" style={{ fontWeight: 600, marginBottom: 8 }}>
          Links
        </Typography>
        <Box display="flex" style={{ gap: 8, flexWrap: 'wrap' }}>
          <TextField
            size="small"
            label="URL *"
            placeholder="https://ejemplo.com"
            value={newUrl}
            onChange={handleUrlChange}
            error={!!urlError}
            helperText={urlError}
            style={{ flex: 2, minWidth: 200 }}
            onKeyPress={(e) => { if (e.key === 'Enter') addLink(); }}
          />
          <TextField
            size="small"
            label="Etiqueta *"
            placeholder="Descripción del link"
            value={newLabel}
            onChange={(e) => setNewLabel(e.target.value)}
            style={{ flex: 2, minWidth: 160 }}
            onKeyPress={(e) => { if (e.key === 'Enter') addLink(); }}
          />
          <Button
            variant="contained"
            size="small"
            startIcon={<AddIcon />}
            style={{ backgroundColor: '#6ab3d4', color: '#fff', alignSelf: 'flex-start', marginTop: 2 }}
            onClick={addLink}
            disabled={!newUrl.trim() || !newLabel.trim()}
          >
            Agregar
          </Button>
        </Box>
        {links.length > 0 ? (
          <List dense style={{ marginTop: 8 }}>
            {links.map((link, index) => (
              <ListItem key={index} divider>
                <ListItemIcon style={{ minWidth: 36 }}>
                  <LinkIcon style={{ color: '#6ab3d4' }} />
                </ListItemIcon>
                <ListItemText
                  primary={link.label || link.url}
                  secondary={link.label ? link.url : ''}
                  secondaryTypographyProps={{
                    component: 'a',
                    href: link.url,
                    target: '_blank',
                    rel: 'noopener noreferrer',
                    style: { color: '#6ab3d4' },
                  }}
                />
                <ListItemSecondaryAction>
                  <IconButton edge="end" size="small" onClick={() => removeLink(index)}>
                    <DeleteIcon style={{ color: 'red' }} />
                  </IconButton>
                </ListItemSecondaryAction>
              </ListItem>
            ))}
          </List>
        ) : (
          <Typography variant="body2" color="textSecondary" style={{ marginTop: 8 }}>
            No hay links agregados
          </Typography>
        )}
      </Box>
    </Paper>
  );
};

export default LinksAttachmentsSection;
